import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { X } from "lucide-react";
import { useContext, useState } from "react";
import { CadenceFiltersEditor } from "../connectors/CadenceFiltersEditor";
import { PSScopingContext } from "../connectors/PSScopingContext";
import {
  KNOWN_PARAMS,
  KNOWN_PARAM_KEYS,
  applyOverrideFilters,
  buildEntry,
  getExtraParams,
  readOverrideFilters,
} from "./scheduleEntryModel";
import type { ScheduleEntry } from "./types";

/** The controls for one schedule entry. Its read/write half lives in `scheduleEntryModel`. */

function KnownParamValueControl({
  paramKey,
  value,
  onValueChange,
}: {
  paramKey: string;
  value: string;
  onValueChange: (v: string) => void;
}) {
  const spec = KNOWN_PARAMS[paramKey];
  if (!spec) return null;

  if (spec.type === "select") {
    return (
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="h-7 font-mono text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {spec.options.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      type="number"
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      placeholder="0"
      className="h-7 font-mono text-xs"
    />
  );
}

function ParamRow({
  paramKey,
  value,
  isKnown,
  onKeyChange,
  onValueChange,
  onRemove,
}: {
  paramKey: string;
  value: string;
  isKnown: boolean;
  onKeyChange: (k: string) => void;
  onValueChange: (v: string) => void;
  onRemove: () => void;
}) {
  return (
    <tr>
      <td className="py-0.5 pr-2">
        {isKnown ? (
          <span className="font-mono text-xs">{paramKey}</span>
        ) : (
          <Input
            type="text"
            value={paramKey}
            onChange={(e) => onKeyChange(e.target.value)}
            placeholder="key"
            className="h-7 font-mono text-xs"
          />
        )}
      </td>
      <td className="py-0.5 pr-2">
        {isKnown ? (
          <KnownParamValueControl paramKey={paramKey} value={value} onValueChange={onValueChange} />
        ) : (
          <Input
            type="text"
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder="value"
            className="h-7 font-mono text-xs"
          />
        )}
      </td>
      <td className="py-0.5">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 hover:bg-destructive/20"
          onClick={onRemove}
          aria-label={`Remove ${paramKey || "parameter"}`}
        >
          <X className="h-3 w-3" />
        </Button>
      </td>
    </tr>
  );
}

function CadenceParamsEditor({
  params,
  availableKnown,
  onKeyChange,
  onValueChange,
  onRemove,
  onAdd,
}: {
  params: [string, string][];
  availableKnown: string[];
  onKeyChange: (index: number, key: string) => void;
  onValueChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
  onAdd: (key: string) => void;
}) {
  const [addingParam, setAddingParam] = useState<string | null>(null);

  function handleAddSelect(value: string) {
    onAdd(value === "__custom__" ? "" : value);
    setAddingParam(null);
  }

  return (
    <div>
      {params.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground">
              <th className="pb-1 text-left font-medium">Parameter</th>
              <th className="pb-1 text-left font-medium">Value</th>
              <th className="w-6" />
            </tr>
          </thead>
          <tbody>
            {params.map(([k, v], i) => (
              <ParamRow
                key={i}
                paramKey={k}
                value={v}
                isKnown={k in KNOWN_PARAMS}
                onKeyChange={(nk) => onKeyChange(i, nk)}
                onValueChange={(nv) => onValueChange(i, nv)}
                onRemove={() => onRemove(i)}
              />
            ))}
          </tbody>
        </table>
      )}

      {addingParam !== null ? (
        <div className="mt-1">
          <Select onValueChange={handleAddSelect}>
            <SelectTrigger className="h-7 w-48 text-xs">
              <SelectValue placeholder="Choose parameter…" />
            </SelectTrigger>
            <SelectContent>
              {availableKnown.map((k) => (
                <SelectItem key={k} value={k}>
                  {k}
                </SelectItem>
              ))}
              <SelectItem value="__custom__">Custom…</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-1 gap-1 text-xs text-muted-foreground"
          onClick={() => setAddingParam("")}
        >
          Add parameter
        </Button>
      )}
    </div>
  );
}

/**
 * SIM-1369: Typed editor for required runtime params on a single schedule
 * entry. The keys are locked (declared by the table's base config); only the
 * values are editable.
 */
export function RequiredParamsEditor({
  requiredParams,
  values,
  onChange,
}: {
  requiredParams: string[];
  values: Record<string, string | number> | undefined;
  onChange: (next: Record<string, string | number>) => void;
}) {
  function handleValueChange(key: string, raw: string) {
    const next: Record<string, string | number> = { ...(values ?? {}) };
    if (raw === "") {
      delete next[key];
    } else {
      const num = Number(raw);
      // Preserve numeric type when the input parses cleanly as a finite number.
      next[key] = !isNaN(num) && raw.trim() !== "" && isFinite(num) ? num : raw;
    }
    onChange(next);
  }

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-muted-foreground">
          <th className="pb-1 text-left font-medium">Required param</th>
          <th className="pb-1 text-left font-medium">Value</th>
        </tr>
      </thead>
      <tbody>
        {requiredParams.map((key) => {
          const raw = values?.[key];
          const displayValue = raw === undefined || raw === null ? "" : String(raw);
          return (
            <tr key={key}>
              <td className="py-0.5 pr-2">
                <span className="font-mono text-xs">{key}</span>
              </td>
              <td className="py-0.5 pr-2">
                <Input
                  type="text"
                  value={displayValue}
                  onChange={(e) => handleValueChange(key, e.target.value)}
                  placeholder={`${key} value`}
                  className="h-7 font-mono text-xs"
                  aria-label={`${key} value`}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function MissingRequiredMessage({ missing }: { missing: string[] }) {
  if (missing.length === 0) return null;
  return (
    <div className="mt-1 text-xs text-destructive" role="alert">
      {missing.length === 1
        ? `${missing[0]} is required for this table.`
        : `${missing.join(", ")} are required for this table.`}
    </div>
  );
}

/**
 * One entry's generic params and, where the connector has project scoping, its filter
 * overrides. The filters editor needs `PSScopingContext` for its inherited-value readout,
 * which is why the provider sits above the schedules panel rather than beside it.
 */
export function ScheduleEntryExtraEditors({
  entry,
  tableName,
  tableFields,
  onChange,
}: {
  entry: ScheduleEntry;
  tableName: string;
  tableFields?: string[];
  onChange: (next: ScheduleEntry) => void;
}) {
  const psScoping = useContext(PSScopingContext);
  const params = getExtraParams(entry);
  const usedKeys = new Set(params.map(([k]) => k));
  const availableKnown = KNOWN_PARAM_KEYS.filter((k) => !usedKeys.has(k));

  function updateParams(newParams: [string, string][]) {
    onChange(buildEntry(entry, tableName, newParams));
  }

  function handleParamChange(index: number, key: string, value: string) {
    const p = getExtraParams(entry);
    p[index] = [key, value];
    updateParams(p);
  }

  return (
    <div className="mt-2 space-y-2">
      <CadenceParamsEditor
        params={params}
        availableKnown={availableKnown}
        onKeyChange={(i, k) => handleParamChange(i, k, params[i]?.[1] ?? "")}
        onValueChange={(i, v) => handleParamChange(i, params[i]?.[0] ?? "", v)}
        onRemove={(i) => updateParams(getExtraParams(entry).filter((_, j) => j !== i))}
        onAdd={(k) => updateParams([...getExtraParams(entry), [k, ""]])}
      />
      {psScoping && (
        <CadenceFiltersEditor
          filters={readOverrideFilters(entry)}
          inherited={{
            schoolids: psScoping.projectSchoolIds,
            sinceDate: psScoping.projectSinceDate,
            source: "project",
          }}
          schoolYearStart={psScoping.schoolYearStart}
          tableName={tableName}
          tableFields={tableFields}
          onChange={(next) => onChange(applyOverrideFilters(entry, next))}
        />
      )}
    </div>
  );
}
